import React, {
  CSSProperties, forwardRef, ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import MapboxMap, * as Mapbox from 'react-map-gl/mapbox';
import {MapRef, ViewStateChangeEvent} from 'react-map-gl/mapbox';
import mapboxgl, {Map as MapboxGLMap} from "mapbox-gl";
import ngage from "../../services/ngage";
import useMapStyle from "./useMapStyle";
import {useQuery} from "react-query";
import StyleControl from "./StyleControl";
import LayerControl from "./LayerControl";
import {BeaconLayer, ExhibitLayer, FacilityLayer, SiteLayer, TrailLayer, ZoneLayer} from "./Layers";
import {FeatureCollection, Geometry} from "geojson";
import {featureCollection} from "../../services/geometry";
import {SitumBuildingSource} from "./Sources/SitumBuildingSource";
import {useMapLayers} from "./useMapLayers";
import {MapLayer} from "../../types/MapLayers";
import FloatingDropdown from "../FloatingDropdown";
import {Site} from "../../types/Site";
import {flyToBounds} from "../../services/map";
import {useOperator} from "../../context/OperatorContext";
import InteractionAreaLayer from "./Layers/InteractionAreaLayer";
import {SearchBox} from "@mapbox/search-js-react";

// Constants ----

const ACCESS_TOKEN = process.env["REACT_APP_MAPBOX_ACCESS_TOKEN"];
const DEFAULT_LONGITUDE = -2.895;
const DEFAULT_LATITUDE = 54.093;
const DEFAULT_ZOOM = 5;

// Helpers ----

const mapImages: MapImage[] = [
  {name: "ngage-pin-beacon", path: "/pins/beacon.png"},
  {name: "ngage-pin-event", path: "/pins/event.png"},
  {name: "ngage-pin-exhibit", path: "/pins/exhibit.png"},
  {name: "ngage-pin-exhibit-live", path: "/pins/exhibit-live.png"},
  {name: "ngage-pin-facility", path: "/pins/facility.png"},
  {name: "ngage-pin-location", path: "/pins/location.png"},
  {name: "ngage-pin-trail", path: "/pins/trail.png"},
  {name: "ngage-pin-waypoint", path: "/pins/waypoint.png"},
  {name: "ngage-pin-zone", path: "/pins/zone.png"},
]

function loadMapImages(map: MapboxGLMap) {
  mapImages.forEach(({name, path}) => {
    map.loadImage(path, (error, image) => {
      // Show loading error in console
      if (error) console.warn(`[Map] ${error.message}`)

      // Add the image to the app
      if (!!image && !map.hasImage(name)) map.addImage(name, image)
    })
  })
}

function hidePointsOfInterestLayers(map: MapboxGLMap) {
  map.getStyle()?.layers.forEach(layer => {
    if (layer.id.startsWith("poi-")) map.removeLayer(layer.id)
  })
}

function filterCollection(collection?: FeatureCollection, excludeFeatureIds?: string[]): FeatureCollection {
  const allFeatures = collection?.features ?? []

  return featureCollection(
    excludeFeatureIds? allFeatures.filter(feature => !excludeFeatureIds.includes(String(feature.id))) : allFeatures
  )
}

function flyToSiteArea(site: Site, ref: RefObject<MapRef> | ((instance: MapRef | null) => void)) {
  if (ref) flyToBounds(site.area, ref as RefObject<MapRef>);
}

// Types ----

type SiteItem = {
  label: string
  value: string | null
}

type MapImage = {
  name: string
  path: string
}

type MapStyle = {
  name: string
  url: string
}

type MapProps = {
  height: CSSProperties['height']
  defaultMapStyle?: MapStyle | string | null
  initialViewState?: Mapbox.MapProps['initialViewState'] | null
  onViewChanged?: (event: ViewStateChangeEvent) => void
  hidePointsOfInterest?: boolean
  hideFeatures?: string[]
  childLayersFirst?: boolean
  buildingId?: number | null
  floorId?: number | null
  showSiteSelector?: boolean
  showSearchBox?: boolean
  onSelectSite?: (siteId: number | null) => void
  flyToMode?: 'site' | 'bounds'
  flyToGeometry?: Geometry
  siteId?: number | null
  children?: ReactNode
}

// Component ----

// @ts-ignore
const Map = forwardRef(({height, defaultMapStyle, initialViewState, onViewChanged, hidePointsOfInterest, hideFeatures, childLayersFirst, buildingId, floorId, children, onSelectSite, showSiteSelector = false, showSearchBox = false, flyToMode = 'site', siteId, flyToGeometry} : MapProps, ref?: RefObject<MapRef>) => {
  const {isMultiSite} = useOperator();
  const {findMapStyle} = useMapStyle();
  const {enabledMapLayers} = useMapLayers();

  const defaultMapRef = useRef<MapRef>(null);
  const mapRef = ref || defaultMapRef;

  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchReady, setSearchReady] = useState<MapboxGLMap | null>(null);

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);
  const {data: sites} = useQuery(['sites.all'], () => ngage.sites.all());
  const sitesData = sites?.data;

  // Keep track of the currently selected map style
  const [mapStyle, setMapStyle] = useState<MapStyle>(findMapStyle(defaultMapStyle));

  // Keep track of the currently enabled layers
  const [enabledLayers, setEnabledLayers] = useState<MapLayer[]>(enabledMapLayers);

  // Fetch custom map feature data
  const {data: collection} = useQuery(['maps.interactive'], ngage.maps.interactive);

  // Filter out any excluded features
  const filteredCollection = filterCollection(collection, hideFeatures ?? []);

  // Keep track of the last flown site ID to avoid map flying back to the selected site on re-render
  const lastFlownSiteId = useRef<number | null>(null);

  // Initialize selected site once data arrives
  useEffect(() => {
    setSelectedSiteId(siteId ?? sitesData?.[0].id ?? null);
  }, [siteId, sitesData]);

  // Notify parent when selected site ID changes
  useEffect(() => {
    onSelectSite?.(selectedSiteId);
  }, [selectedSiteId, onSelectSite]);

  // Fly to the bounds if flyToGeometry is provided and flyToMode is 'bounds'
  useEffect(() => {
    if (flyToMode !== 'bounds' || !flyToGeometry) return;
    flyToBounds(flyToGeometry, mapRef);
  }, [flyToGeometry, flyToMode, mapRef])

  // Fly map to site on mount and when selected ID actually changes and when the site selector is shown
  useEffect(() => {
    if (!showSiteSelector || flyToMode !== 'site' || !mapLoaded || !selectedSiteId) return;

    const site = sitesData?.find((site) => site.id === selectedSiteId);

    if (!site) return;
    if (lastFlownSiteId.current === site.id) return;

    lastFlownSiteId.current = site.id;
    flyToSiteArea(site, mapRef);
  }, [showSiteSelector, selectedSiteId, sitesData, mapLoaded, flyToMode, mapRef]);

  // Callback to prepare map on first load
  const prepareMap = useCallback((map: MapboxGLMap) => {
    setMapLoaded(true);
    loadMapImages(map);

    if (hidePointsOfInterest) {
      hidePointsOfInterestLayers(map);
    }
  }, [hidePointsOfInterest]);

  // Prepare the fallback initial view state
  const defaultViewState = {
    longitude: DEFAULT_LONGITUDE,
    latitude: DEFAULT_LATITUDE,
    zoom: DEFAULT_ZOOM,
    padding: {top: 10, bottom: 10, left: 10, right: 10}
  }

  // Get site items for dropdown
  const siteItems = sitesData ? getSiteItems(sitesData) : [];

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    return sites.map(site => ({label: site.name, value: site.id.toString()}));
  }

  function handleSelectSite(id: number) {
    const site = sitesData?.find(site => site.id === id);

    if (site) {
      flyToSiteArea(site, mapRef);
      onSelectSite?.(site.id);
      setSelectedSiteId(site.id);
    }
  }

  const searchBoxTheme = {
    variables: {
      boxShadow: 'none',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
    },
  }

  return (
    <div className='flex flex-col gap-3'>
      {/* Search box */}
      {(showSearchBox && searchReady) && (
        <div className='max-w-[50ch]'>
          <SearchBox
            accessToken={ACCESS_TOKEN as string}
            map={searchReady}
            mapboxgl={mapboxgl}
            value={searchInputValue}
            onChange={setSearchInputValue}
            placeholder='Search for a location'
            theme={searchBoxTheme}
          />
        </div>
      )}

      <MapboxMap
        mapboxAccessToken={ACCESS_TOKEN}
        mapStyle={mapStyle.url}
        initialViewState={initialViewState ?? defaultViewState}
        onLoad={({ target }) => {
          prepareMap(target);
          setSearchReady(target);
        }}
        onStyleData={({target}: any) => prepareMap(target)} // TODO: Remove any type after upgrading from mapbox-gl 3.14.0
        onDragEnd={onViewChanged}
        onZoomEnd={onViewChanged}
        style={{height, borderRadius: 6}}
        cooperativeGestures
        ref={mapRef}
      >
        {/* Site selector */}
        {(isMultiSite && showSiteSelector) &&
          <div className="absolute right-[50px] top-[8px] max-w-60">
            <FloatingDropdown
              items={siteItems}
              selectedIndex={Math.max(siteItems.findIndex((item) => item.value === selectedSiteId?.toString()), 0)}
              onSelect={(item) => handleSelectSite(Number(item.value))}
            />
          </div>
        }

        {/* Show Mapbox map controls */}
        <Mapbox.FullscreenControl/>
        <Mapbox.NavigationControl showCompass visualizePitch/>
        <Mapbox.GeolocateControl trackUserLocation showUserLocation showUserHeading showAccuracyCircle={false}/>


        {/* Show custom map controls */}
        <StyleControl selected={mapStyle} onSelect={setMapStyle}/>
        <LayerControl selected={enabledLayers} onSelect={setEnabledLayers}/>

        {/* Render additional layers */}
        {childLayersFirst && children}

        {/* Show site feature layers */}
        <Mapbox.Source type="geojson" id="ngage-site-features" data={filteredCollection}>
          <SiteLayer enabledLayers={enabledLayers} />
          <ZoneLayer enabledLayers={enabledLayers} />
          <InteractionAreaLayer enabledLayers={enabledLayers} />
          <ExhibitLayer enabledLayers={enabledLayers} floorId={floorId}/>
          <FacilityLayer enabledLayers={enabledLayers} floorId={floorId}/>
          <TrailLayer enabledLayers={enabledLayers} />
          <BeaconLayer enabledLayers={enabledLayers} />
        </Mapbox.Source>

        {/* Show building layers */}
        {(buildingId && floorId) &&
          <SitumBuildingSource buildingId={buildingId} floorId={floorId} enabledLayers={enabledLayers} />
        }

        {/* Render additional layers */}
        {!childLayersFirst && children}
      </MapboxMap>
    </div>
  );
});

export default Map;